import { ErrorOutlineRounded } from '@mui/icons-material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import {
  Button,
  FormControl,
  FormControlProps,
  FormHelperText,
  FormLabel,
  IconButton,
  List,
  Stack,
} from '@mui/material';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import { PropsWithChildren, useRef } from 'react';
import { StrictModeDroppable } from '../../DragAndDrop';

interface Props {
  label: string;
  fullWidth?: FormControlProps['fullWidth'];
  onAdd: () => void;
  addButtonLabel: string;
  margin?: FormControlProps['margin'];
  error?: string;
  name: string;
  onDragEnd: (result: DropResult) => void;
  enableDragAndDrop: boolean;
}

export default function DraggableFieldArray({
  label,
  fullWidth = false,
  onAdd,
  addButtonLabel,
  children,
  margin,
  error,
  name,
  onDragEnd,
  enableDragAndDrop = false,
}: PropsWithChildren<Props>) {
  const helperTextId = error && `${name}-helperText`;
  const controlId = `${name}-id`;
  const isProcessingRef = useRef(false);

  const handleDragStart = () => {
    if (isProcessingRef.current) {
      return;
    }
    isProcessingRef.current = true;
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || !onDragEnd) {
      isProcessingRef.current = false;
      return;
    }

    if (result.source.index === result.destination.index) {
      isProcessingRef.current = false;
      return;
    }

    onDragEnd(result);
    requestAnimationFrame(() => {
      isProcessingRef.current = false;
    });
  };

  const content = (
    <FormControl
      id={controlId}
      error={!!error}
      component="fieldset"
      fullWidth={fullWidth}
      margin={margin}
    >
      <Stack gap={1} justifyItems={'flex-start'}>
        <FormLabel component="legend" error={!!error}>
          {label}
        </FormLabel>
        {enableDragAndDrop ? (
          <StrictModeDroppable droppableId={name}>
            {(provided, snapshot) => (
              <List
                dense
                disablePadding
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  backgroundColor: snapshot.isDraggingOver
                    ? (theme) => theme.palette.action.hover
                    : 'transparent',
                  borderRadius: snapshot.isDraggingOver ? 1 : 0,
                  transition: 'background-color 0.2s ease',
                  padding: 1,
                }}
              >
                <Stack gap={1}>{children}</Stack>
                {provided.placeholder}
              </List>
            )}
          </StrictModeDroppable>
        ) : (
          <List dense disablePadding>
            {children}
          </List>
        )}
        <div>
          <Button
            variant="text"
            color="inherit"
            startIcon={
              <IconButton
                component="span"
                size="small"
                sx={{ background: ({ palette }) => palette.gradient.primary, mr: 0.5 }}
              >
                <AddRoundedIcon fontSize="small" htmlColor="white" />
              </IconButton>
            }
            onClick={onAdd}
            sx={{ padding: 1 }}
          >
            {addButtonLabel}
          </Button>
        </div>
      </Stack>
      {error && (
        <Stack direction="row" spacing={1} pt={1} flexWrap="nowrap" alignItems="center">
          <ErrorOutlineRounded color="warning" fontSize="small" />
          <FormHelperText id={helperTextId}>{error}</FormHelperText>
        </Stack>
      )}
    </FormControl>
  );

  if (enableDragAndDrop) {
    return (
      <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        {content}
      </DragDropContext>
    );
  }

  return content;
}
